import styled, { css } from "styled-components";
import { brandTheme } from "../../../theme/brandTokens";
import { modalCloseButton } from '../../../theme/modalClose';

/*
 * The Unibox's scrollbars, as on the Contacts filters panel: a 6px rounded
 * thumb in a soft grey on no track. Only the ::-webkit-scrollbar rules: in
 * Chrome, scrollbar-width / scrollbar-color switch those off for the browser's
 * own thin scrollbar, whose rounded caps were clipped by the panels' rounded
 * corners. The track is inset top and bottom to stay clear of those corners.
 * Firefox, which has no ::-webkit-scrollbar, keeps the standard thin bar.
 */
const thinScrollbar = css`
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: color-mix(in srgb, var(--hd-muted-foreground) 35%, transparent);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: color-mix(in srgb, var(--hd-muted-foreground) 55%, transparent);
  }

  &::-webkit-scrollbar-track {
    margin: 12px 0;
    background: transparent;
  }

  @supports not selector(::-webkit-scrollbar) {
    scrollbar-width: thin;
    scrollbar-color: var(--hd-scrollbar-thumb) transparent;
  }
`;

/* The pills in a thread row and the conversation header: 12px text on a
   24px pill, so they read at normal size, with the status colours from the
   --hd-status-* tokens. */
const chip = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 10px;
  border: 1px solid var(--hd-border);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
`;

const statusChip = css`
  ${chip}

  &.status-open {
    background: var(--hd-muted);
    color: var(--hd-foreground);
  }

  &.status-interested {
    border-color: var(--hd-status-interested-border);
    background: var(--hd-status-interested-bg);
    color: var(--hd-status-interested-fg);
  }

  &.status-meeting {
    border-color: var(--hd-status-meeting-border);
    background: var(--hd-status-meeting-bg);
    color: var(--hd-status-meeting-fg);
  }

  &.status-snoozed {
    border-color: var(--hd-status-snoozed-border);
    background: var(--hd-status-snoozed-bg);
    color: var(--hd-status-snoozed-fg);
  }

  &.status-not-interested {
    border-color: var(--hd-status-declined-border);
    background: var(--hd-status-declined-bg);
    color: var(--hd-status-declined-fg);
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.7;
  }
`;

/* Fills `.children.unibox-layout`, which Layout.styles.js already sizes to the
   viewport minus the TopBar / banner chrome. */
export const StyledUnibox = styled.div`
  ${brandTheme}
  height: 100%;
  min-height: 0;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--hd-background);

  .emptysecrren-holder {
    height: 100%;

    > div {
      height: 100%;
    }
  }
`;

export const StyledUniboxPage = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
  padding: 32px;
  overflow: hidden;
  height: 100%;

  @media (max-width: 767px) {
    padding: 24px 16px;
  }
`;

/* version3: the title block and the Reply Agent button, pb-4 above the panes. */
export const StyledUniboxHeader = styled.section`
  flex-shrink: 0;
  padding-bottom: 16px;

  .header-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
  }

  /* text-2xl font-semibold tracking-tight */
  .page-title {
    margin: 0;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    letter-spacing: -0.025em;
    text-transform: none;
    color: var(--hd-foreground);
  }

  .page-subtitle {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 20px;
    color: var(--hd-muted-foreground);
  }

  /* h-10 rounded-xl, primary into primary/80, shadow-lg shadow-primary/25 */
  .configure-agent-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 16px;
    border: none;
    border-radius: 18px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    color: var(--hd-primary-foreground);
    background: linear-gradient(
      to right,
      var(--hd-primary),
      color-mix(in oklab, var(--hd-primary) 80%, transparent)
    );
    box-shadow:
      0 10px 15px -3px color-mix(in oklab, var(--hd-primary) 25%, transparent),
      0 4px 6px -4px color-mix(in oklab, var(--hd-primary) 25%, transparent);
    cursor: pointer;
    transition:
      box-shadow 0.2s ease,
      filter 0.2s ease;

    &:hover {
      filter: brightness(1.1);
      box-shadow:
        0 20px 25px -5px color-mix(in oklab, var(--hd-primary) 40%, transparent),
        0 8px 10px -6px color-mix(in oklab, var(--hd-primary) 40%, transparent);
    }

    &:hover .sparkle-icon {
      transform: rotate(12deg);
    }

    /* Held back: faded and still; the hover label says why. */
    &[aria-disabled='true'] {
      opacity: 0.65;
      cursor: not-allowed;
      filter: none;
      box-shadow: none;

      .sparkle-icon {
        transform: none;
      }
    }
  }

  .agent-icon-wrap {
    position: relative;
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.2);
  }

  .live-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #34d399;
    box-shadow: 0 0 0 2px var(--hd-primary);
  }

  .sparkle-icon {
    opacity: 0.8;
    transition: transform 0.2s ease;
  }
`;

/* version3's filter rail: rounded-2xl card, p-3, scrolling on its own. */
export const StyledFilterSidebar = styled.aside`
  display: none;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid var(--hd-border);
  border-radius: 22px;
  background: var(--hd-card);
  box-shadow: var(--hd-shadow-card);
  ${thinScrollbar}

  @media (min-width: 1024px) {
    display: flex;
  }

  .rail-search {
    position: relative;
    display: block;
    flex-shrink: 0;

    svg {
      position: absolute;
      top: 50%;
      left: 10px;
      transform: translateY(-50%);
      color: var(--hd-muted-foreground);
      pointer-events: none;
    }

    input {
      width: 100%;
      height: 36px;
      padding: 0 12px 0 32px;
      border: 1px solid var(--hd-border);
      border-radius: 14px;
      font-family: inherit;
      font-size: 14px;
      color: var(--hd-foreground);
      background: var(--hd-background);

      &::placeholder {
        color: var(--hd-muted-foreground);
      }

      &:focus {
        outline: none;
        border-color: color-mix(in srgb, var(--hd-foreground) 20%, transparent);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--hd-foreground) 5%, transparent);
      }
    }
  }

  .rail-divider {
    flex-shrink: 0;
    margin: 12px 0;
    border-top: 1px solid var(--hd-border);
  }

  .rail-heading {
    padding: 0 4px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.025em;
    text-transform: uppercase;
    color: var(--hd-muted-foreground);
  }

  .rail-heading + .rail-group {
    margin-top: 8px;
  }

  .rail-group {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .rail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 6px 10px;
    border: none;
    border-radius: 14px;
    font-family: inherit;
    font-size: 13px;
    line-height: 20px;
    text-align: left;
    color: var(--hd-muted-foreground);
    background: transparent;
    cursor: pointer;
    transition:
      background 0.15s ease,
      color 0.15s ease;

    &.md {
      padding: 8px 10px;
      font-size: 14px;
    }

    &:hover {
      color: var(--hd-foreground);
      background: color-mix(in srgb, var(--hd-muted) 60%, transparent);
    }

    &.active {
      color: var(--hd-foreground);
      background: var(--hd-muted);
    }
  }

  .rail-label {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    svg {
      flex-shrink: 0;
    }
  }

  .rail-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rail-count {
    flex-shrink: 0;
    font-size: 12px;
    color: var(--hd-muted-foreground);
  }

  /* A soft primary pill under the list, the chevron nudging down on hover. */
  .rail-more {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    margin-top: 6px;
    padding: 7px 10px;
    border: 1px dashed color-mix(in oklab, var(--hd-primary) 30%, transparent);
    border-radius: 14px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    color: var(--hd-primary);
    background: color-mix(in oklab, var(--hd-primary) 5%, transparent);
    cursor: pointer;
    transition:
      background 0.15s ease,
      border-color 0.15s ease;

    svg {
      flex-shrink: 0;
      transition: transform 0.15s ease;
    }

    &:hover:not(:disabled) {
      border-style: solid;
      border-color: color-mix(in oklab, var(--hd-primary) 45%, transparent);
      background: color-mix(in oklab, var(--hd-primary) 10%, transparent);

      svg {
        transform: translateY(1px);
      }
    }

    &:disabled {
      cursor: default;
      opacity: 0.75;
    }

    .spin {
      animation: rail-spin 0.9s linear infinite;
    }
  }

  @keyframes rail-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/* grid-cols-1, lg: 224px 340px minmax(0,1fr), gap-6 */
export const StyledThreePaneGrid = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  > * {
    min-height: 0;
    min-width: 0;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 224px 340px minmax(0, 1fr);
    /* A row capped at the grid's own height, so each pane scrolls inside it
       instead of the row growing to its tallest pane and being clipped. */
    grid-template-rows: minmax(0, 1fr);
  }

  /* The list and conversation, laid out as if the wrapper were not there;
     hidden while the filtered-empty message stands in for both. */
  > .panes {
    display: contents;

    &.is-hidden {
      display: none;
    }
  }
`;

/* The composer's account picker: a pill like version3's select, and a menu
   that opens upward from the foot of the pane. */
export const StyledAccountPicker = styled.div`
  position: relative;
  min-width: 0;
  max-width: 100%;

  .ap-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: min(300px, 100%);
    height: 36px;
    padding: 0 10px 0 12px;
    border: 1px solid var(--hd-border);
    border-radius: 12px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    color: var(--hd-foreground);
    background: var(--hd-background);
    cursor: pointer;
    transition:
      background 0.15s ease,
      border-color 0.15s ease,
      box-shadow 0.15s ease;

    &:hover:not(:disabled) {
      background: color-mix(in srgb, var(--hd-muted) 40%, transparent);
    }

    &.open {
      border-color: color-mix(in srgb, var(--hd-foreground) 20%, transparent);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--hd-foreground) 5%, transparent);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .ap-icon {
    flex-shrink: 0;
    color: ${({ $channel }) => ($channel === 'linkedin' ? 'var(--hd-primary)' : 'var(--hd-muted-foreground)')};
  }

  .ap-value {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.placeholder {
      color: var(--hd-muted-foreground);
    }
  }

  .ap-chevron {
    flex-shrink: 0;
    color: var(--hd-muted-foreground);
    transition: transform 0.15s ease;
  }

  .ap-trigger.open .ap-chevron {
    transform: rotate(180deg);
  }

  .ap-menu {
    position: absolute;
    bottom: calc(100% + 6px);
    left: 0;
    z-index: 30;
    width: 280px;
    max-width: calc(100vw - 48px);
    padding: 6px;
    border: 1px solid var(--hd-border);
    border-radius: 18px;
    background: var(--hd-popover, var(--hd-card));
    box-shadow:
      0 10px 15px -3px rgba(15, 23, 42, 0.1),
      0 4px 6px -4px rgba(15, 23, 42, 0.1);
    animation: ap-rise 0.16s ease-out both;
  }

  @keyframes ap-rise {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .ap-menu-head {
    padding: 6px 8px 4px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--hd-muted-foreground);
  }

  .ap-options {
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-height: 220px;
    overflow-y: auto;
    ${thinScrollbar}
  }

  .ap-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 7px 8px;
    border: none;
    border-radius: 12px;
    font-family: inherit;
    font-size: 13px;
    text-align: left;
    color: var(--hd-foreground);
    background: transparent;
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover {
      background: var(--hd-muted);
    }

    &.selected {
      font-weight: 500;
      background: color-mix(in oklab, var(--hd-primary) 7%, transparent);
    }
  }

  .ap-option-icon {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 22px;
    height: 22px;
    border-radius: 8px;
    color: ${({ $channel }) => ($channel === 'linkedin' ? 'var(--hd-primary)' : 'var(--hd-muted-foreground)')};
    background: var(--hd-muted);
  }

  .ap-option.selected .ap-option-icon {
    color: var(--hd-primary);
    background: color-mix(in oklab, var(--hd-primary) 12%, transparent);
  }

  .ap-option-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ap-check {
    flex-shrink: 0;
    color: var(--hd-primary);
  }

  .ap-clear {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    margin-top: 4px;
    padding: 7px 8px;
    border: none;
    border-top: 1px solid var(--hd-border);
    border-radius: 0 0 12px 12px;
    font-family: inherit;
    font-size: 12px;
    color: var(--hd-muted-foreground);
    background: transparent;
    cursor: pointer;

    &:hover {
      color: var(--hd-foreground);
    }
  }
`;

/* The filtered-empty message, spanning the list and conversation columns. */
export const StyledFilteredEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 48px 24px;
  border: 1px solid var(--hd-border);
  border-radius: 22px;
  background:
    radial-gradient(ellipse at 50% 35%, color-mix(in oklab, var(--hd-primary) 5%, transparent), transparent 60%),
    var(--hd-card);
  box-shadow: var(--hd-shadow-card);

  @media (min-width: 1024px) {
    grid-column: 2 / -1;
  }

  .fe-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    /* Wide enough for the one-sentence messages to sit on a single line. */
    max-width: 440px;
    text-align: center;
  }

  .fe-icon {
    display: grid;
    place-items: center;
    width: 52px;
    height: 52px;
    border-radius: 18px;
    color: var(--hd-primary);
    background: var(--hd-gradient-soft);
    box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--hd-primary) 12%, transparent);
  }

  .fe-title {
    margin: 18px 0 0;
    font-size: 17px;
    line-height: 24px;
    font-weight: 600;
    letter-spacing: -0.015em;
    text-transform: none;
    color: var(--hd-foreground);
    text-wrap: balance;
  }

  .fe-text {
    margin: 6px 0 0;
    font-size: 13.5px;
    line-height: 1.6;
    color: var(--hd-muted-foreground);
    text-wrap: balance;
  }

  .fe-clear {
    margin-top: 20px;
    height: 36px;
    padding: 0 16px;
    border: 1px solid var(--hd-border);
    border-radius: 14px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    color: var(--hd-foreground);
    background: var(--hd-background);
    cursor: pointer;
    transition:
      background 0.15s ease,
      border-color 0.15s ease;

    &:hover {
      border-color: color-mix(in srgb, var(--hd-foreground) 20%, transparent);
      background: var(--hd-muted);
    }
  }
`;

/* Reply Agent modal. Mirrors ReplyAgentModal.tsx in the Remix reference.

   The two-surface structure is what makes it read correctly in dark: the panel
   itself sits on --hd-background (bg-background) while the section cards and
   the enabled pill sit on --hd-card, which is one step lighter. Inputs drop
   back to --hd-background so they read as recessed. Putting the panel on
   --hd-card instead flattens all three into one tone. */
export const StyledReplyAgentModal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  @media (min-width: 640px) {
    padding: 32px;
  }

  .modal-backdrop {
    position: absolute;
    inset: 0;
    border: none;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    cursor: pointer;
  }

  .modal-panel {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 768px;
    max-height: 90vh;
    overflow: hidden;
    border-radius: 22px;
    border: 1px solid var(--hd-border);
    background: var(--hd-background);
    /* shadow-2xl */
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }

  .modal-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px;
    border-bottom: 1px solid var(--hd-border);
  }

  .modal-head-left {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .modal-icon {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 18px;
    color: var(--hd-primary);
    background: color-mix(in srgb, var(--hd-primary) 10%, transparent);
  }

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.025em;
    color: var(--hd-foreground);
  }

  p {
    margin: 2px 0 0;
    max-width: 448px;
    font-size: 12px;
    color: var(--hd-muted-foreground);
  }

  .modal-head-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .enabled-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border: 1px solid var(--hd-border);
    border-radius: 14px;
    font-size: 12px;
    font-weight: 500;
    background: var(--hd-card);
  }

  .pill-toggle {
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    width: 36px;
    height: 20px;
    padding: 0;
    border: none;
    border-radius: 999px;
    background: var(--hd-muted);
    cursor: pointer;
    transition: background 0.15s ease;

    &.on {
      background: var(--hd-primary);
    }

    .pill-knob {
      display: block;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: var(--hd-background);
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.2);
      transform: translateX(2px);
      transition: transform 0.15s ease;
    }

    &.on .pill-knob {
      transform: translateX(16px);
    }
  }

  .close-btn {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 14px;
    color: var(--hd-muted-foreground);
    background: transparent;
    cursor: pointer;

    &:hover {
      background: var(--hd-muted);
      color: var(--hd-foreground);
    }
  }

  .modal-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px 24px;
    overflow-y: auto;
    ${thinScrollbar}

  }

  .agent-card {
    padding: 16px;
    border: 1px solid var(--hd-border);
    border-radius: 22px;
    background: var(--hd-card);

    .agent-card-head {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--hd-primary);
    }

    h3 {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      color: var(--hd-foreground);
    }

    .agent-card-desc {
      margin: 2px 0 0;
      max-width: none;
      font-size: 12px;
      color: var(--hd-muted-foreground);
    }

    .agent-card-body {
      margin-top: 12px;
    }
  }

  .autonomy-grid {
    display: grid;
    gap: 8px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  .autonomy-option {
    padding: 12px;
    border: 1px solid var(--hd-border);
    border-radius: 18px;
    text-align: left;
    background: var(--hd-card);
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

    &:hover {
      border-color: color-mix(in srgb, var(--hd-foreground) 20%, transparent);
    }

    &.active {
      border-color: var(--hd-primary);
      background: color-mix(in srgb, var(--hd-primary) 5%, transparent);
      /* ring-2 ring-primary/20 */
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--hd-primary) 20%, transparent);
    }

    .autonomy-option-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      font-size: 14px;
      font-weight: 600;
      color: var(--hd-foreground);

      svg {
        flex-shrink: 0;
        color: var(--hd-primary);
      }
    }

    .autonomy-option-desc {
      margin: 4px 0 0;
      max-width: none;
      font-size: 12px;
      color: var(--hd-muted-foreground);
    }
  }

  .agent-textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid var(--hd-border);
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    resize: none;
    color: var(--hd-foreground);
    background: var(--hd-background);

    &:focus {
      outline: none;
      border-color: color-mix(in srgb, var(--hd-foreground) 20%, transparent);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--hd-foreground) 5%, transparent);
    }
  }

  .agent-field {
    margin-top: 12px;

    .agent-field-label {
      display: block;
      font-size: 12px;
      font-weight: 500;
      color: var(--hd-muted-foreground);
    }

    .agent-field-note {
      font-size: 10px;
      color: color-mix(in srgb, var(--hd-muted-foreground) 60%, transparent);
    }

    .agent-input {
      width: 100%;
      height: 36px;
      margin-top: 4px;
      padding: 0 12px;
      border: 1px solid var(--hd-border);
      border-radius: 14px;
      font-family: inherit;
      font-size: 14px;
      color: var(--hd-foreground);
      background: var(--hd-background);

      &:focus {
        outline: none;
        border-color: color-mix(in srgb, var(--hd-foreground) 20%, transparent);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--hd-foreground) 5%, transparent);
      }
    }
  }

  .channel-grid {
    display: grid;
    gap: 8px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .channel-toggle {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
    border: 1px solid var(--hd-border);
    border-radius: 18px;
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;

    &.on {
      border-color: color-mix(in srgb, var(--hd-primary) 40%, transparent);
      background: color-mix(in srgb, var(--hd-primary) 3%, transparent);
    }

    .channel-toggle-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      font-weight: 500;
      color: var(--hd-foreground);
    }

    .channel-toggle-desc {
      margin: 0;
      max-width: none;
      font-size: 12px;
      color: var(--hd-muted-foreground);
    }
  }

  .meeting-toggle-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
    border: 1px solid var(--hd-border);
    border-radius: 18px;
    cursor: pointer;

    .meeting-toggle-title {
      font-size: 14px;
      font-weight: 500;
      color: var(--hd-foreground);
    }

    .meeting-toggle-desc {
      margin: 0;
      max-width: none;
      font-size: 12px;
      color: var(--hd-muted-foreground);
    }
  }

  .timing-grid {
    display: grid;
    gap: 12px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .agent-field {
      margin-top: 0;
    }

    input[type='number'] {
      font-variant-numeric: tabular-nums;
    }
  }

  .guardrail-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      font-size: 14px;
      color: var(--hd-foreground);

      svg {
        flex-shrink: 0;
        margin-top: 2px;
        color: #10b981;
      }
    }
  }

  .modal-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 24px;
    border-top: 1px solid var(--hd-border);
    /* bg-card/50 — a half-strength card tint over the panel, so the footer
       lifts slightly in both themes instead of staying light-slate in dark. */
    background: color-mix(in srgb, var(--hd-card) 50%, transparent);
  }

  .secondary-btn,
  .primary-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 16px;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
  }

  .secondary-btn {
    border: 1px solid var(--hd-border);
    background: var(--hd-background);
    color: var(--hd-foreground);

    &:hover {
      background: var(--hd-muted);
    }
  }

  .primary-btn {
    border: none;
    background: var(--hd-foreground);
    /* text-background, not white: in dark the button fill is near-white, so a
       white label would be invisible. */
    color: var(--hd-background);
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.9;
    }
  }
  /* Same close button as every other modal (theme/modalClose). */
  .close-btn {
    ${modalCloseButton}
  }

`;

/**
 * The row at the end of the thread list while the next page loads: three
 * dots in the text colour rising and fading in turn. In the list's own flow,
 * after the last thread, so it never covers a thread.
 */
export const StyledListLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0 24px;
  animation: uniboxListLoaderIn 0.2s ease-out;

  .dots {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 12px;
  }

  .dots span {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: var(--hd-foreground);
    animation: uniboxListLoaderDot 1.1s ease-in-out infinite;
  }

  .dots span:nth-child(2) {
    animation-delay: 0.15s;
  }

  .dots span:nth-child(3) {
    animation-delay: 0.3s;
  }

  @keyframes uniboxListLoaderDot {
    0%,
    70%,
    100% {
      opacity: 0.25;
      transform: translateY(0) scale(0.85);
    }
    35% {
      opacity: 1;
      transform: translateY(-4px) scale(1);
    }
  }

  @keyframes uniboxListLoaderIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .dots span {
      animation-duration: 2.2s;
    }
  }
`;

export const StyledDotsLoader = styled.div`
  padding: 20px 0 20px;
  display: flex;
  justify-content: center;
  /* Fades out over whatever surface it overlays, so it must follow the theme
     rather than assume a white pane. The 0%-opacity stop keeps the fade in the
     card's own hue instead of drifting through grey. */
  background: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--hd-card) 99%, transparent),
    color-mix(in srgb, var(--hd-card) 0%, transparent)
  );
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 500px;
  pointer-events: none;

  ${({ $loaderBottom }) =>
    $loaderBottom &&
    css`
      align-items: flex-end;
      top: auto;
      bottom: 0;
      background: linear-gradient(
        to top,
        color-mix(in srgb, var(--hd-card) 99%, transparent),
        color-mix(in srgb, var(--hd-card) 0%, transparent)
      );
      height: 300px;
    `}

  /* For the inverse (column-reverse) message list. Absolute positioning is
     useless there: the padding-box origin maps to the bottom of a reversed
     flow, so top: 0 lands off-screen. Sticky pins to the scrollport edge
     instead, which is direction-agnostic. */
  .dots-loader {
    width: 50px;
    aspect-ratio: 2;
    --_g: no-repeat
      radial-gradient(circle closest-side, var(--hd-loader-dot) 90%, #0000);
    background:
      var(--_g) 0% 50%,
      var(--_g) 50% 50%,
      var(--_g) 100% 50%;
    background-size: calc(100% / 3) 50%;
    animation: l3 1s infinite linear;
  }

  @keyframes l3 {
    20% {
      background-position:
        0% 0%,
        50% 50%,
        100% 50%;
    }
    40% {
      background-position:
        0% 100%,
        50% 0%,
        100% 50%;
    }
    60% {
      background-position:
        0% 50%,
        50% 100%,
        100% 0%;
    }
    80% {
      background-position:
        0% 50%,
        50% 50%,
        100% 100%;
    }
  }
`;

export const StyledContentHolder = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  position: relative;
  border: 1px solid var(--hd-border);
  border-radius: 22px;
  background: var(--hd-card);
  box-shadow: var(--hd-shadow-card);

  /* EmailSidebar wraps the head and the list in this div, so it — not the
     holder — is what .col-holder's flex: 1 resolves against. Left unstyled it
     was a plain block: .col-holder then had auto height, never overflowed, and
     so never produced a scrollbar; the list just grew until the holder's
     overflow: hidden cut it off mid-thread. */
  .emails-sidebar {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
  }

  .thread-pane-head {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid var(--hd-border);
  }

  .thread-count {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--hd-muted-foreground);
  }

  .sort-btn {
    display: inline-flex;
    align-items: center;
    border: none;
    background: transparent;
    font-size: 11px;
    font-weight: 500;
    color: var(--hd-muted-foreground);
    cursor: pointer;

    &:hover {
      color: var(--hd-foreground);
    }
  }

  .col-holder {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: scroll;
    position: relative;
    /* Matches the messages pane so both panes scroll the same way. */
    ${thinScrollbar}

  }

  /* border-border/60 — was a hardcoded light slate, which read as a bright
     white rule once the pane went dark. */
  .thread-divider {
    margin: 0 16px;
    border-bottom: 1px solid color-mix(in srgb, var(--hd-border) 60%, transparent);
  }

  .no-found {
    padding: 64px 24px;
    text-align: center;
    font-size: 14px;
    color: var(--hd-muted-foreground);
  }

  .infinite-scroll-component {
    overflow: hidden;
  }
`;

export const StyledThreadListItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-left: 2px solid transparent;
  text-align: left;
  cursor: pointer;
  background: transparent;
  transition: background 0.15s ease, border-color 0.15s ease;

  /* version3: the selected row keeps the card colour and is marked by its
     primary left edge; other rows tint on hover. */
  ${({ $active }) =>
    $active
      ? css`
          border-left-color: var(--hd-primary);
          background: var(--hd-card);
        `
      : css`
          &:hover:not([disabled]) {
            background: color-mix(in srgb, var(--hd-muted) 40%, transparent);
          }
        `}

  .thread-row-top {
    display: flex;
    width: 100%;
    align-items: flex-start;
    gap: 10px;
  }

  .avatar {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    font-size: 12px;
    font-weight: 600;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .thread-main {
    min-width: 0;
    flex: 1;
  }

  .thread-name-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .thread-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: ${({ $unread }) => ($unread ? 600 : 500)};
    color: var(--hd-foreground);
    text-transform: capitalize;
  }

  .thread-time {
    flex-shrink: 0;
    font-size: 12px;
    color: var(--hd-muted-foreground);
  }

  .thread-company {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: var(--hd-muted-foreground);
  }

  .thread-preview {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-size: 13px;
    line-height: 1.5;
    /* Unread was var(--black) — literally #000, invisible on a dark pane. */
    color: ${({ $unread }) => ($unread ? 'var(--hd-foreground)' : 'var(--hd-muted-foreground)')};
  }

  .thread-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
  }

  .channel-chip,
  .campaign-chip {
    ${chip}
    flex-shrink: 0;
  }

  /* bg-background, so the chip reads a step darker than the pane it sits on.
     On --hd-card it was the same tone as its surroundings and disappeared. */
  .channel-chip.email {
    color: var(--hd-muted-foreground);
    background: var(--hd-background);
  }

  /* version3 marks LinkedIn in the brand colour, not LinkedIn blue. */
  .channel-chip.linkedin {
    border-color: color-mix(in srgb, var(--hd-primary) 25%, transparent);
    color: var(--hd-primary);
    background: color-mix(in srgb, var(--hd-primary) 6%, transparent);
  }

  .status-chip {
    ${statusChip}
    flex-shrink: 0;
  }

  .campaign-chip {
    flex-shrink: 1;
    min-width: 0;
    margin-left: auto;
    color: var(--hd-muted-foreground);
    background: var(--hd-background);

    svg {
      flex-shrink: 0;
    }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .unread-badge {
    flex-shrink: 0;
    min-width: 20px;
    padding: 3px 6px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    text-align: center;
    color: var(--hd-primary-foreground);
    background: var(--hd-primary);
  }

  .star-btn {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 28px;
    height: 28px;
    margin: -5px -6px 0 0;
    border: none;
    border-radius: 10px;
    background: transparent;
    padding: 0;
    /* Muted outline until starred, in whichever theme is active. */
    color: color-mix(in srgb, var(--hd-muted-foreground) 55%, transparent);
    cursor: pointer;

    &:hover {
      background: var(--hd-muted);
    }

    &.active {
      color: #f3e400;
    }
  }
`;

export const StyledEmailMessages = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  position: relative;
  border: 1px solid var(--hd-border);
  border-radius: 22px;
  background: var(--hd-card);
  box-shadow: var(--hd-shadow-card);

  .empty-state-pane,
  .no-messages-found {
    flex: 1;
    display: grid;
    place-items: center;
    padding: 64px 24px;
    font-size: 14px;
    color: var(--hd-muted-foreground);
  }

  .conversation-head {
    flex-shrink: 0;
    padding: 16px 24px;
    border-bottom: 1px solid var(--hd-border);
    background: var(--hd-card);
  }

  .conversation-head-inner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  /* Lead identity in the email header — avatar + name + company, the same
     three things the thread rows show. */
  .email-head-profile {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .email-head-text {
    min-width: 0;
  }

  .head-lead-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 15px;
    font-weight: 600;
    text-transform: capitalize;
    color: var(--hd-foreground);
  }

  .head-lead-company {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: var(--hd-muted-foreground);
  }

  .head-avatar-wrap {
    position: relative;
    flex-shrink: 0;
  }

  .head-avatar {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    overflow: hidden;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 600;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .head-channel-badge {
    position: absolute;
    right: -2px;
    bottom: -2px;
    display: grid;
    place-items: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    color: var(--hd-muted-foreground);
    background: var(--hd-card);
    box-shadow: 0 0 0 1px var(--hd-border);
  }

  .linkedin-head-profile {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .linkedin-avatar-wrap {
    position: relative;
    display: block;
  }

  .linkedin-badge {
    position: absolute;
    right: -2px;
    bottom: -2px;
    display: grid;
    place-items: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--hd-card);
    box-shadow: 0 0 0 1px var(--hd-border);
    color: var(--hd-primary);
  }

  .linkedin-head-name {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.025em;
    text-transform: capitalize;
    color: var(--hd-foreground);

    .linkedin-degree {
      font-size: 12px;
      font-weight: 500;
      color: var(--hd-muted-foreground);
    }
  }

  .linkedin-head-title {
    font-size: 12px;
    color: var(--hd-muted-foreground);
  }

  .lead-profile-link {
    color: inherit;
    text-decoration: none;

    &[href]:hover {
      text-decoration: underline;
    }
  }

  .linkedin-head-sender {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    font-size: 12px;
    color: var(--hd-muted-foreground);

    img {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      object-fit: cover;
    }

    strong {
      font-weight: 600;
      color: var(--hd-foreground);
    }
  }

  .conversation-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  .status-chip {
    ${statusChip}
  }

  .icon-action-btn {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 12px;
    color: var(--hd-muted-foreground);
    background: transparent;
    cursor: pointer;

    /* Faded and inert, with the reason in the tooltip. These read as live
       controls otherwise, and clicking one did nothing at all. */
    &:disabled {
      cursor: not-allowed;
      opacity: 0.4;

      &:hover {
        background: transparent;
        color: var(--hd-muted-foreground);
      }
    }

    &:hover {
      background: var(--hd-muted);
      color: var(--hd-foreground);
    }

    /* The conversation header's star matches the thread list's: a faded outline
       until starred, yellow once it is. A solid dark star read as "already
       starred" on threads that were not. */
    &.star-action {
      color: color-mix(in srgb, var(--hd-muted-foreground) 55%, transparent);

      &:hover {
        color: var(--hd-muted-foreground);
      }
    }

    &.active-star,
    &.active-star:hover {
      color: #f3e400;
    }
  }

  .action-divider {
    width: 1px;
    height: 20px;
    background: var(--hd-border);
    margin: 0 4px;
  }

  /* The email thread's subject, on a muted band under the header. */
  .subject-strip {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
    padding: 8px 24px;
    border-bottom: 1px solid var(--hd-border);
    background: color-mix(in srgb, var(--hd-muted) 30%, transparent);
  }

  .subject-strip-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: color-mix(in srgb, var(--hd-muted-foreground) 60%, transparent);
  }

  .subject-strip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12.5px;
    font-weight: 500;
    color: color-mix(in srgb, var(--hd-foreground) 90%, transparent);
  }

  .messages-wrapper {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    position: relative;
  }

  /* Deliberately NOT position: relative — the InfiniteScroll loader is absolute,
     and anchoring it here would resolve against this reversed scroller's padding
     box (which maps to the bottom of a column-reverse flow, i.e. off-screen).
     Leaving it static anchors the loader to .messages-wrapper, pinning it to the
     top of the pane instead. */
  .messages-holder {
    flex: 1;
    min-height: 0;
    /* column-reverse keeps the newest message pinned to the bottom without any
       JS scroll management, and is what InfiniteScroll's inverse mode expects. */
    display: flex;
    flex-direction: column-reverse;
    overflow-x: hidden;
    overflow-y: scroll;
    padding: 16px 20px;
    background: var(--hd-card);
    ${thinScrollbar}

  }

  .transcript-stack {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .message-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .transcript-loading {
    display: flex;
    justify-content: center;
    padding: 8px 0 16px;
  }

  .date-separator {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 4px;

    &::before,
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: var(--hd-border);
    }

    span {
      padding: 2px 10px;
      border-radius: 999px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.14em;
      color: var(--hd-muted-foreground);
      background: var(--hd-muted);
    }
  }

  .email-opened {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 12px;

    .icon-holder {
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #e8e9f1;
      border-radius: 50%;
    }

    .email-heading {
      font-weight: 400;
      font-size: 14px;
      color: var(--hd-muted-foreground);
    }
  }

  .input-holder {
    flex-shrink: 0;
    padding: 12px;
    border-top: 1px solid var(--hd-border);
    background: var(--hd-card);
  }

  .composer-toolbar-top {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding-bottom: 10px;
  }

  .reply-channel-switch {
    display: flex;
    gap: 2px;
    padding: 2px;
    border: 1px solid var(--hd-border);
    border-radius: 14px;
    background: var(--hd-muted);
  }

  .reply-type-holder {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 12px;
    border: none;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 500;
    color: var(--hd-muted-foreground);
    background: transparent;
    cursor: pointer;

    &.active {
      color: ${({ $replyLinkedinActive }) => ($replyLinkedinActive ? 'var(--hd-primary)' : 'var(--hd-foreground)')};
      background: var(--hd-card);
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
    }
  }

  .draft-ai-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    margin-left: auto;
    padding: 0 12px;
    border: none;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 500;
    color: var(--hd-primary);
    background: linear-gradient(
      to right,
      color-mix(in oklab, var(--hd-primary) 10%, transparent),
      color-mix(in oklab, var(--hd-primary) 5%, transparent)
    );
    cursor: pointer;

    &:hover {
      background: linear-gradient(
        to right,
        color-mix(in oklab, var(--hd-primary) 15%, transparent),
        color-mix(in oklab, var(--hd-primary) 10%, transparent)
      );
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .composer-box {
    overflow: hidden;
    border: 1px solid var(--hd-border);
    border-radius: 22px;
    background: var(--hd-background);
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);

    &:focus-within {
      border-color: color-mix(in srgb, var(--hd-foreground) 20%, transparent);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--hd-foreground) 5%, transparent);
    }
  }

  .composer-head {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 16px;
    border-bottom: 1px solid color-mix(in srgb, var(--hd-border) 60%, transparent);
    background: var(--hd-card);
    font-size: 12px;

    .field-label {
      flex-shrink: 0;
      width: 40px;
      color: var(--hd-muted-foreground);
    }
  }

  .composer-parties {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 16px;
  }

  .composer-party {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .party-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    overflow: hidden;
    padding: 2px 10px;
    border: 1px solid var(--hd-border);
    border-radius: 999px;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--hd-foreground);
    background: var(--hd-background);

    .party-icon {
      flex-shrink: 0;
      color: var(--hd-muted-foreground);
    }
  }

  /* line-height 1, or the pill's 12px line box pushes the initials off centre. */
  .party-avatar {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    font-size: 7.5px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0;
  }

  .composer-subject {
    display: flex;
    align-items: center;
    gap: 8px;

    .field-label {
      width: 48px;
    }

    /* Editable, but sits in the line like the read-only parts do; it shows
       it can be typed in on hover and focus. */
    .field-input {
      flex: 1;
      min-width: 0;
      padding: 2px 6px;
      margin-left: -6px;
      border: 1px solid transparent;
      border-radius: 10px;
      font: inherit;
      font-weight: 500;
      color: var(--hd-foreground);
      background: transparent;
      transition:
        border-color 0.15s ease,
        background 0.15s ease;

      &:hover:not(:disabled) {
        border-color: var(--hd-border);
      }

      &:focus {
        outline: none;
        border-color: var(--hd-ring);
        background: var(--hd-background);
      }

      &:disabled {
        color: var(--hd-muted-foreground);
        cursor: not-allowed;
      }
    }
  }

  .textarea {
    width: 100%;
    min-height: ${({ $isLinkedin }) => ($isLinkedin ? '72px' : '96px')};
    padding: 14px 16px;
    border: none;
    outline: none;
    resize: none;
    font-family: inherit;
    font-size: ${({ $isLinkedin }) => ($isLinkedin ? '14px' : '14.5px')};
    line-height: 1.7;
    color: var(--hd-foreground);
    background: transparent;

    &::placeholder {
      color: var(--hd-muted-foreground);
    }
  }

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 6px 12px;
    border-top: 1px solid var(--hd-border);
  }

  .composer-icon-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .composer-icon-btn {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 14px;
    color: var(--hd-muted-foreground);
    background: transparent;
    cursor: pointer;

    /* Parked. No hover response either — a control that lights up under the
       pointer is one that claims it can be used. */
    &:disabled {
      cursor: not-allowed;
      opacity: 0.4;

      &:hover {
        background: transparent;
        color: var(--hd-muted-foreground);
      }
    }

    &:hover {
      background: var(--hd-muted);
      color: var(--hd-foreground);
    }
  }

  .btn-submit-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-grow: 1;
  }

  .save-draft-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border: none;
    border-radius: 14px;
    font-size: 13px;
    font-weight: 500;
    color: var(--hd-muted-foreground);
    background: transparent;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.4;

      &:hover {
        color: var(--hd-muted-foreground);
      }
    }

    &:hover {
      color: var(--hd-foreground);
    }
  }

  .send-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 14px;
    border: none;
    border-radius: 14px;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    cursor: pointer;
    background: ${({ $isLinkedin }) => ($isLinkedin ? 'var(--hd-gradient-brand)' : 'var(--hd-gradient-houdiny)')};
    box-shadow: var(--hd-shadow-card);
    transition: transform 0.15s ease;

    &:hover:not(:disabled) {
      transform: scale(1.02);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

/* The voice note inside a bubble: on the brand gradient it is drawn in white,
   on the lead's white bubble in the primary colour. */
export const StyledVoiceNote = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 240px;
  max-width: 100%;
  padding: 2px 8px 2px 2px;

  --vn-accent: ${({ $outbound }) => ($outbound ? '#fff' : 'var(--hd-primary)')};
  --vn-rest: ${({ $outbound }) =>
    $outbound ? 'rgba(255, 255, 255, 0.45)' : 'color-mix(in srgb, var(--hd-muted-foreground) 35%, transparent)'};

  audio {
    display: none;
  }

  .vn-play {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 50%;
    color: ${({ $outbound }) => ($outbound ? 'var(--hd-primary)' : '#fff')};
    background: var(--vn-accent);
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.18);
    cursor: pointer;
    transition: transform 0.15s ease;

    svg {
      fill: currentColor;
    }

    &:hover:not(:disabled) {
      transform: scale(1.06);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .vn-wave {
    display: flex;
    flex: 1;
    align-items: center;
    gap: 2px;
    height: 24px;
    min-width: 0;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }
  }

  .vn-bar {
    flex: 1;
    min-width: 2px;
    border-radius: 999px;
    background: var(--vn-rest);
    transition: background 0.15s ease;

    &.played {
      background: var(--vn-accent);
    }
  }

  .vn-time {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${({ $outbound }) => ($outbound ? 'rgba(255, 255, 255, 0.9)' : 'var(--hd-muted-foreground)')};
  }
`;

export const StyledMessage = styled.div`
  /* version3's transcript bubble, for email and LinkedIn alike: the lead on the
     left in a bordered bubble, you on the right in the brand gradient. */
  ${({ $variant }) =>
    $variant === 'bubble' &&
    css`
      display: flex;
      align-items: flex-end;
      gap: 8px;
      justify-content: ${({ $outbound }) => ($outbound ? 'flex-end' : 'flex-start')};
    `}

  .bubble-col {
    display: flex;
    max-width: 72%;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
    align-items: ${({ $outbound }) => ($outbound ? 'flex-end' : 'flex-start')};

    &.email {
      max-width: 74%;
    }
  }

  .bubble-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    padding: 0 4px;
    font-size: 10px;
    color: var(--hd-muted-foreground);

    .author {
      flex-shrink: 0;
      font-weight: 600;
      color: color-mix(in srgb, var(--hd-foreground) 80%, transparent);
    }

    .subject {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: color-mix(in srgb, var(--hd-muted-foreground) 80%, transparent);
    }
  }

  .bubble-body {
    max-width: 100%;
    padding: 8px 14px;
    font-size: 13px;
    line-height: 1.375;
    overflow-wrap: anywhere;

    ${({ $outbound, $variant }) =>
      $variant === 'bubble' &&
      ($outbound
        ? css`
            border-radius: 22px 22px 10px 22px;
            color: #fff;
            background: var(--hd-gradient-brand);
            box-shadow: 0 1px 2px rgba(180, 40, 20, 0.22);

            a,
            p,
            .email-message,
            .email-message p {
              color: #fff;
            }
          `
        : css`
            border: 1px solid var(--hd-border);
            border-radius: 22px 22px 22px 10px;
            color: var(--hd-foreground);
            background: var(--hd-card);
          `)}

    /* An attachment sits closer to the bubble's edge than text does. */
    &.has-media {
      padding: 6px;
    }

    .media-attachment {
      img,
      video {
        display: block;
        max-width: 100%;
        border-radius: 16px;
      }
    }

    /* The email formatter's wrapper, flattened into the bubble's own type. */
    .email-message {
      display: block;
      padding: 0;
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
      color: inherit;

      p {
        margin: 0;
        color: inherit;
      }

      a {
        word-break: break-all;
        text-decoration: underline;
      }

      ol {
        padding-left: 20px;
        list-style: decimal;
      }

      ul {
        padding-left: 20px;
        list-style: disc;
      }
    }
  }

  .bubble-status {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0 4px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--hd-primary);
  }

  /* h-6 w-6, text-[8px] */
  .bubble-avatar {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    overflow: hidden;
    font-size: 8px;
    font-weight: 600;

    &.outbound {
      color: #fff;
      background: var(--hd-gradient-houdiny);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  /* Legacy layout used by LeadPreview communication tab */
  ${({ $variant, $send, $threadType }) =>
    !$variant &&
    css`
      position: relative;
      padding: 30px 10px 10px 55px;
      color: #2c2c2e;
      border-bottom: 1px solid #ddd;

      .message-head {
        display: flex;
        align-items: start;
        justify-content: space-between;
        gap: 10px;
        margin: 0 0 12px;

        .img-box {
          position: absolute;
          top: 28px;
          left: 0;
          width: 40px;
          height: 40px;
          border-radius: 50px;
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

        .user-name-holder {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0 0 5px;
        }

        .email-heading {
          font-weight: 500;
          color: var(--hd-foreground);
        }

        .email-subheading {
          color: var(--blue);
          margin: 0 0 8px;
        }

        .email-subject {
          font-size: 14px;
          color: var(--hd-foreground);
        }

        .date-and-time {
          display: flex;
          gap: 10px;
        }

        .linkedin-icon-holder {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12px;
          padding: 4px 5px;
          border-radius: 5px;
          background: ${$threadType === 'linkedin' ? 'var(--blue)' : '#ececec'};
          color: ${$threadType === 'linkedin' ? 'var(--white)' : '#3f3f3f'};
        }
      }

      .email-message {
        display: block;
        font-size: 14px;
        line-height: 20px;
        color: #000;

        p {
          margin: 0;
          color: #000;
        }

        a {
          word-break: break-all;
          text-decoration: underline;
          color: var(--blue);
        }
      }

      ${$send &&
      css`
        margin: 0 15px 32px 0;
        align-self: flex-end;
        color: var(--white);
        background: var(--blue);

        &::before {
          content: '';
          position: absolute;
          top: 0;
          right: -15px;
          border-top: 17px solid var(--blue);
          border-left: 16px solid transparent;
          border-right: 16px solid transparent;
        }

        .message-head {
          display: none;
        }

        .email-message p,
        .email-message a {
          color: var(--white) !important;
        }
      `}
    `}
`;
